import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpenIcon, 
  CurrencyDollarIcon, 
  UserGroupIcon,
  DocumentTextIcon,
  CalendarIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  CalendarDaysIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const FamilyResourcesPage = () => {

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">
              Family Resources
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Everything you need to know about East End Prep athletics - from policies and procedures to important dates and forms.
            </p>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Student-Athlete Handbook */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <BookOpenIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Student-Athlete Handbook
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Complete guide to policies, procedures, and expectations for all student-athletes.
              </p>
              <Link
                to="/family-resources/handbook"
                className="btn-primary inline-flex items-center"
              >
                View Handbook
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Athletic Fees */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <CurrencyDollarIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Athletic Fees
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Information about athletic fees, payment options, and required forms.
              </p>
              <Link
                to="/family-resources/fees"
                className="btn-primary inline-flex items-center"
              >
                View Fees
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Parent Information */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <UserGroupIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Parent Information
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Guidelines for parent involvement, communication, and volunteer opportunities.
              </p>
              <Link
                to="/family-resources/parent-info"
                className="btn-primary inline-flex items-center"
              >
                Learn More
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Academic Requirements */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <AcademicCapIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Academic Requirements
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Eligibility requirements, academic support services, and important policies.
              </p>
              <Link
                to="/family-resources/academic"
                className="btn-primary inline-flex items-center"
              >
                View Requirements
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Important Dates */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <CalendarDaysIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Important Dates
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Key dates for registration, physicals, parent meetings, and season schedules.
              </p>
              <Link
                to="/family-resources/important-dates"
                className="btn-primary inline-flex items-center"
              >
                View Calendar
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* FAQ */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-4">
                <QuestionMarkCircleIcon className="w-8 h-8 text-primary mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">
                  Frequently Asked Questions
                </h3>
              </div>
              <p className="text-gray-600 mb-4">
                Answers to common questions about athletics, eligibility, and participation.
              </p>
              <Link
                to="/family-resources/faq"
                className="btn-primary inline-flex items-center"
              >
                View FAQ
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Link>
            </div>
         </div>
       </div>

      {/* Contact Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Additional Help?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              If you can't find what you're looking for, don't hesitate to reach out to our athletic department.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center justify-center"
              >
                Contact Athletic Department
              </Link>
              <a
                href="mailto:athletics@eastendprep.edu"
                className="btn-secondary inline-flex items-center justify-center"
              >
                Email Us Directly
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FamilyResourcesPage;